import { useEffect, useRef, useState } from "react";
import { APIProvider, Map, Marker, useMap } from "@vis.gl/react-google-maps";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { Alert, Box, Paper, Typography } from "@mui/material";
import { useAdminDriverTracking } from "../hooks/useAdminDriverTracking";
import { useDriverMobile } from "../hooks/useDriverMobile";

const GOOGLE_MAPS_API_KEY = "AIzaSyBIbtiiqiEJ6F2ScRafjfv1SmpaeWg3NDQ";
const DEFAULT_CENTER = { lat: -16.409047, lng: -71.537451 };
function hasValidPosition(driver) {
  return (
    Number.isFinite(Number(driver.latitude)) &&
    Number.isFinite(Number(driver.longitude)) &&
    Number(driver.latitude) >= -90 &&
    Number(driver.latitude) <= 90 &&
    Number(driver.longitude) >= -180 &&
    Number(driver.longitude) <= 180
  );
}

function DriverMarkers({ drivers, selectedDriverId, onSelectDriver }) {
  const map = useMap();
  const didFitInitialDrivers = useRef(false);
  const selectedDriver = drivers.find(
    (driver) => driver.driverId === selectedDriverId,
  );

  useEffect(() => {
    if (!map) return;

    if (selectedDriver && hasValidPosition(selectedDriver)) {
      map.panTo({
        lat: Number(selectedDriver.latitude),
        lng: Number(selectedDriver.longitude),
      });
      return;
    }

    if (didFitInitialDrivers.current) return;
    const locatedDrivers = drivers.filter(hasValidPosition);
    if (locatedDrivers.length === 0) return;

    if (locatedDrivers.length === 1) {
      map.setCenter({
        lat: Number(locatedDrivers[0].latitude),
        lng: Number(locatedDrivers[0].longitude),
      });
      map.setZoom(13);
    } else {
      const positions = locatedDrivers.map((driver) => ({
        lat: Number(driver.latitude),
        lng: Number(driver.longitude),
      }));
      map.fitBounds(
        {
          north: Math.max(...positions.map(({ lat }) => lat)),
          south: Math.min(...positions.map(({ lat }) => lat)),
          east: Math.max(...positions.map(({ lng }) => lng)),
          west: Math.min(...positions.map(({ lng }) => lng)),
        },
        72,
      );
    }
    didFitInitialDrivers.current = true;
  }, [drivers, map, selectedDriver]);

  return drivers.filter(hasValidPosition).map((driver) => (
    <Marker
      key={driver.driverId}
      position={{ lat: Number(driver.latitude), lng: Number(driver.longitude) }}
      title={`Conductor #${driver.driverId} · ${driver.status === "working" ? "En servicio" : "Disponible"}`}
      onClick={() => onSelectDriver(driver.driverId)}
      options={{
        icon: {
          url: "/assets/marker/car_on.png",
          scaledSize: new window.google.maps.Size(48, 48),
          anchor: new window.google.maps.Point(24, 44),
        },
        zIndex: driver.driverId === selectedDriverId ? 2 : 1,
      }}
    />
  ));
}

function formatUpdatedAt(updatedAt) {
  const timestamp = Date.parse(updatedAt || "");
  if (!Number.isFinite(timestamp)) return "Sin actualización registrada";

  return new Intl.DateTimeFormat("es-PE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

export function TrackDriversScreen() {
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const { drivers, connectionError } = useAdminDriverTracking(0);
  const selectedDriver = drivers.find(
    (driver) => driver.driverId === selectedDriverId,
  );
  const {
    driver: driverDetails,
    vehicle: selectedVehicle,
    loading,
    error,
    loadDriver,
  } = useDriverMobile(selectedDriver?.vehicleId);

  const handleSelectDriver = (driverId) => {
    setSelectedDriverId(driverId);
    loadDriver(driverId);
  };
  const availableCount = drivers.filter(
    (driver) => driver.status === "on",
  ).length;
  const workingCount = drivers.filter(
    (driver) => driver.status === "working",
  ).length;

  useEffect(() => {
    if (selectedDriverId && !selectedDriver) setSelectedDriverId(null);
  }, [selectedDriver, selectedDriverId]);

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <Box
        component="main"
        sx={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          bgcolor: "#e8e8e8",
        }}
      >
        <Map
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={13}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          style={{ width: "100%", height: "100%" }}
        >
          <DriverMarkers
            drivers={drivers}
            selectedDriverId={selectedDriverId}
            onSelectDriver={handleSelectDriver}
          />
        </Map>

        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            top: 4,
            left: "50%",
            transform: "translateX(-50%)",
            width: "calc(100% - 32px)",
            maxWidth: 460,
            boxSizing: "border-box",
            p: "5px 5px",
            borderRadius: "12px",
            bgcolor: "#222225",
            color: "#fff",
            boxShadow: "0 12px 28px rgba(0, 0, 0, 0.32)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: 18, color: "#d5d5d9" }}>
              <Box component="span" sx={{ color: "#18b9e8", fontWeight: 700 }}>
                {availableCount}
              </Box>{" "}
              disponibles
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 18, color: "#d5d5d9" }}>
              <Box component="span" sx={{ color: "#f5a623", fontWeight: 700 }}>
                {workingCount}
              </Box>{" "}
              en servicio
            </Typography>
          </Box>
          {connectionError && (
            <Alert
              severity="error"
              sx={{ mt: 1.5, py: 0, borderRadius: 2, fontSize: 13 }}
            >
              {connectionError}
            </Alert>
          )}
        </Paper>

        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            left: "50%",
            bottom: 16,
            transform: "translateX(-50%)",
            width: "min(460px, calc(100% - 32px))",
            boxSizing: "border-box",
            p: "22px 24px",
            borderRadius: "12px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            bgcolor: "#222225",
            color: "#fff",
            boxShadow: "0 12px 28px rgba(0, 0, 0, 0.32)",
          }}
        >
          <Box sx={{ pr: 2, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#a5a6ad",
                fontWeight: 700,
                letterSpacing: "0.08em",
              }}
            >
              CONDUCTOR
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25 }}
            >
              <PersonOutlineRoundedIcon
                sx={{ color: "#18b9e8", fontSize: 20 }}
              />
              <Typography
                variant="body2"
                role="status"
                sx={{
                  color: "#f7f7f8",
                  fontWeight: 600,
                  overflowWrap: "anywhere",
                }}
              >
                {!selectedDriver
                  ? "Selecciona un conductor"
                  : loading
                    ? "Cargando…"
                    : error ||
                      [driverDetails?.name, driverDetails?.lastname]
                        .filter(Boolean)
                        .join(" ") ||
                      "Sin nombre registrado"}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: "#d5d5d9", mt: 1.5, overflowWrap: "anywhere" }}
            >
              Placa: {selectedDriver && loading
                ? "Cargando…"
                : (selectedDriver && selectedVehicle?.plate) || "—"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#d5d5d9", mt: 0.5, overflowWrap: "anywhere" }}
            >
              Modelo: {selectedDriver && loading
                ? "Cargando…"
                : (selectedDriver && selectedVehicle?.model) || "—"}
            </Typography>
          </Box>
          <Box sx={{ pl: 2.5, minWidth: 0, borderLeft: "1px solid #39393f" }}>
            <Typography
              variant="caption"
              sx={{
                color: "#a5a6ad",
                fontWeight: 700,
                letterSpacing: "0.08em",
              }}
            >
              ÚLTIMA UBICACIÓN
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25 }}
            >
              <PlaceOutlinedIcon sx={{ color: "#18b9e8", fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ color: "#f7f7f8", fontWeight: 600 }}
              >
                {selectedDriver
                  ? formatUpdatedAt(selectedDriver.updatedAt)
                  : "—"}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </APIProvider>
  );
}
